import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { ENDPOINT } from '../api/auth';
import './sales.css';

const bankOptions = ['CBE', 'Awash', 'Dashen', 'Abyssinia', 'Birhan', 'Telebirr', 'Check'];

const Sales = () => {
  // State variables
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [comment, setComment] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [selectedBank, setSelectedBank] = useState('');
  const [cashAmount, setCashAmount] = useState('');
  const [bankAmount, setBankAmount] = useState('');
  const [customer, setCustomer] = useState('');
  const [unpaidAmount, setUnpaidAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [processing, setProcessing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [categories, setCategories] = useState([]);
  const [secondaryPaymentMethod, setSecondaryPaymentMethod] = useState('cash');
  const [secondarySelectedBank, setSecondarySelectedBank] = useState('');
  const [dailySales, setDailySales] = useState([]);
  const [showDailySales, setShowDailySales] = useState(false);
  const [transactionDate, setTransactionDate] = useState(new Date().toISOString().split('T')[0]);

  // Fetch products from API
  const fetchProducts = async () => {
    try {
      setLoading(true);
      console.log('Fetching products...');
      const response = await axios.post(
        ENDPOINT,
        {},
        {
          params: { action: 'getProducts' },
        }
      );
      
      console.log('Products API response:', response.data);
      
      if (!response.data.products) {
        console.error('No products array in response:', response.data);
        toast.error('Invalid products data received');
        return;
      }

      // Match React Native filtering logic: only include products that are in store and have quantity > 0
      const availableProducts = response.data.products.filter(
        (product) => product.status === 'in_store' && Number(product.quantity) > 0
      );
      
      console.log('Available products:', availableProducts);
      setProducts(availableProducts);
    } catch (err) {
      console.error("Fetch products error:", err);
      toast.error(err.response?.data?.message || 'Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const fetchDailySales = async () => {
    try {
      const response = await axios.post(
        ENDPOINT,
        {},
        {
          params: { action: 'get_daily_sales' },
        }
      );
      if (response.data.success) {
        setDailySales(response.data.sales);
      } else {
        toast.error(response.data.message || 'Failed to fetch daily sales');
      }
    } catch (error) {
      console.error('Fetch daily sales error:', error);
      const errorMessage = error.response?.data?.message || 'An error occurred while fetching daily sales.';
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchDailySales();
  }, []);

  useEffect(() => {
    if (products.length > 0) {
      const uniqueCategories = ['All', ...new Set(products.map(p => p.category))];
      setCategories(uniqueCategories);
    }
  }, [products]);

  // Cart management functions
  const addToCart = (product) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.product_id === product.id);
      if (existingItem) {
        return prevCart.map(item => 
          item.product_id === product.id 
            ? {...item, quantity: item.quantity + 1} 
            : item
        );
      }
      return [...prevCart, {
        product_id: product.id,
        name: product.name,
        quantity: 1,
        price: product.selling_price,
        maxQuantity: product.quantity
      }];
    });
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.product_id !== productId));
  };

  const updateCartItem = (productId, field, value) => {
    setCart(prevCart => 
      prevCart.map(item => 
        item.product_id === productId ? {...item, [field]: value} : item
      )
    );
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => 
      total + (item.quantity * item.price), 0
    ).toFixed(2);
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    if (!transactionDate) {
      toast.error('Please select a transaction date');
      return;
    }

    if (paymentMethod === 'credit' && !customer) {
      toast.error('Please enter customer name for credit payment');
      return;
    }

    // Validate partial payment: if bank amount is provided, bank must be selected
    if (paymentMethod === 'partial') {
      const bankAmt = parseFloat(bankAmount || 0);
      if (bankAmt > 0 && !selectedBank) {
        toast.error('Please select a bank for the bank transfer portion');
        return;
      }
    }

    setProcessing(true);
    
    try {
      console.log('Sending payment data:', {
        paymentMethod,
        cashAmount,
        bankAmount,
        selectedBank,
        customerName: customer,
        unpaidAmount,
        comment,
        products: cart.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          price: item.price
        }))
      });

      const response = await axios.post(
        ENDPOINT,
        {
          action: 'checkout',
          cart: JSON.stringify(cart),
          payment_method: paymentMethod,
          bank_name: paymentMethod === 'bank' || (paymentMethod === 'partial' && parseFloat(bankAmount) > 0) ? selectedBank : 
                    (paymentMethod === 'credit' && secondaryPaymentMethod === 'bank') ? secondarySelectedBank : '',
          cash_amount: paymentMethod === 'partial' ? cashAmount : null,
          bank_amount: paymentMethod === 'partial' ? bankAmount : null,
          customer_name: customer,
          unpaid_amount: unpaidAmount,
          secondary_payment_method: paymentMethod === 'credit' ? secondaryPaymentMethod : null,
          comment: comment,
          transaction_date: transactionDate
        }
      );

      if (response.data.success) {
        toast.success('Checkout completed successfully');
        const resetForm = () => {
          setCart([]);
          setComment('');
          setPaymentMethod('cash');
          setSelectedBank('');
          setCashAmount('');
          setBankAmount('');
          setCustomer('');
          setUnpaidAmount('');
          setTransactionDate(new Date().toISOString().split('T')[0]);
          setSecondaryPaymentMethod('cash');
          setSecondarySelectedBank('');
        };
        resetForm();
        fetchProducts();
        fetchDailySales();
      } else {
        toast.error(response.data.message || 'Checkout failed');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error(err.response?.data?.message || 'Checkout failed');
    } finally {
      setProcessing(false);
    }
  };

  const getFilteredProducts = () => {
    let filtered = [...products];
    
    // Apply category filter
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }
    
    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(query) ||
        (product.description && product.description.toLowerCase().includes(query))
      );
    }
    
    return filtered;
  };

  const deleteSale = async (saleId) => {
    try {
      const response = await axios.post(
        ENDPOINT,
        { sale_id: saleId },
        {
          params: { action: 'delete_sale' },
        }
      );

      if (response.data.success) {
        toast.success('Sale deleted successfully');
        // Refresh daily sales after deletion
        fetchDailySales();
      } else {
        toast.error(response.data.message || 'Failed to delete sale');
      }
    } catch (error) {
      console.error('Delete sale error:', error);
      const errorMessage = error.response?.data?.message || 'An error occurred while deleting the sale.';
      toast.error(errorMessage);
    }
  };

  const confirmDelete = (saleId) => {
    if (window.confirm('Are you sure you want to delete this sale?')) {
      deleteSale(saleId);
    }
  };

  return (
    <div className="sales-page">
      <div className="page-header">
        <h2>Sales</h2>
        <button 
          className="toggle-button" 
          onClick={() => setShowDailySales(!showDailySales)}
        >
          {showDailySales ? 'Hide' : 'Show'} Daily Sales
        </button>
      </div>

      {showDailySales && (
        <div className="daily-sales-container">
          <h3>Today's Sales</h3>
          {dailySales.length === 0 ? (
            <p>No sales recorded for today.</p>
          ) : (
            <table className="sales-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Quantity</th>
                  <th>Price</th>
                  <th>Total</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {dailySales.map((item) => (
                  <tr key={item.transaction_item_id}>
                    <td>{item.product_name}</td>
                    <td>{item.quantity}</td>
                    <td>{item.price}</td>
                    <td>{item.total_amount}</td>
                    <td>
                      <button 
                        className="delete-button"
                        onClick={() => confirmDelete(item.transaction_item_id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="sales-content">
        <div className="products-section">
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button 
              className="search-button"
              onClick={fetchProducts}
            >
              Refresh
            </button>
          </div>

          <div className="category-filter">
            {categories.map(category => (
              <button
                key={category}
                className={`category-button ${selectedCategory === category ? 'selected' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="loading">Loading products...</div>
          ) : (
            <div className="products-grid">
              {getFilteredProducts().map(product => (
                <div key={product.id} className="product-card">
                  <div className="product-content">
                    <h4 className="product-name">{product.name}</h4>
                    <p className="product-price">{product.selling_price} ETB</p>
                    <p className="product-stock">Available: {product.quantity}</p>
                  </div>
                  <button 
                    className="add-to-cart-button"
                    onClick={() => addToCart(product)}
                    disabled={processing}
                  >
                    Add to Cart
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="cart-section">
          <h3>Shopping Cart</h3>
          {cart.length === 0 ? (
            <p>Your cart is empty</p>
          ) : (
            <>
              <div className="cart-items">
                {cart.map(item => (
                  <div key={item.product_id} className="cart-item">
                    <div className="cart-item-info">
                      <span className="cart-item-name">{item.name}</span>
                      <input
                        type="number"
                        className="cart-item-price"
                        value={item.price}
                        onChange={(e) => {
                          const num = parseFloat(e.target.value) || 0;
                          updateCartItem(item.product_id, 'price', Math.max(0, num));
                        }}
                        min="0"
                      />
                    </div>
                    <div className="cart-item-controls">
                      <button 
                        onClick={() => updateCartItem(item.product_id, 'quantity', Math.max(1, item.quantity - 1))}
                        className="quantity-button"
                      >
                        -
                      </button>
                      <input
                        type="number"
                        className="quantity-input"
                        value={item.quantity}
                        onChange={(e) => {
                          const num = parseInt(e.target.value) || 1;
                          updateCartItem(item.product_id, 'quantity', Math.min(num, item.maxQuantity));
                        }}
                        min="1"
                        max={item.maxQuantity}
                      />
                      <button 
                        onClick={() => updateCartItem(item.product_id, 'quantity', Math.min(item.quantity + 1, item.maxQuantity))}
                        className="quantity-button"
                      >
                        +
                      </button>
                      <button 
                        onClick={() => removeFromCart(item.product_id)}
                        className="remove-button"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="cart-total">
                <h4>Total: {calculateTotal()} ETB</h4>
              </div>

              <div className="payment-section">
                <h3>Payment Method</h3>
                <div className="payment-methods">
                  <button 
                    className={`payment-method-button ${paymentMethod === 'cash' ? 'selected' : ''}`}
                    onClick={() => setPaymentMethod('cash')}
                  >
                    Cash
                  </button>
                  <button 
                    className={`payment-method-button ${paymentMethod === 'bank' ? 'selected' : ''}`}
                    onClick={() => setPaymentMethod('bank')}
                  >
                    Bank
                  </button>
                  <button 
                    className={`payment-method-button ${paymentMethod === 'credit' ? 'selected' : ''}`}
                    onClick={() => setPaymentMethod('credit')}
                  >
                    Credit
                  </button>
                  <button 
                    className={`payment-method-button ${paymentMethod === 'partial' ? 'selected' : ''}`}
                    onClick={() => setPaymentMethod('partial')}
                  >
                    Partial
                  </button>
                </div>
                
                {(paymentMethod === 'bank' || paymentMethod === 'partial') && (
                  <div className="bank-options">
                    {bankOptions.map(bank => (
                      <button
                        key={bank}
                        className={`bank-option ${selectedBank === bank ? 'selected' : ''}`}
                        onClick={() => setSelectedBank(bank)}
                      >
                        {bank}
                      </button>
                    ))}
                  </div>
                )}

                {paymentMethod === 'partial' && (
                  <div className="partial-payment">
                    <input
                      type="number"
                      className="payment-input"
                      placeholder="Cash Amount"
                      value={cashAmount}
                      onChange={(e) => setCashAmount(e.target.value)}
                    />
                    <input
                      type="number"
                      className="payment-input"
                      placeholder="Bank Amount"
                      value={bankAmount}
                      onChange={(e) => setBankAmount(e.target.value)}
                    />
                  </div>
                )}
                
                {paymentMethod === 'credit' && (
                  <div className="credit-container">
                    <h4>Credit Payment Details</h4>
                    <input
                      type="text"
                      className="payment-input"
                      placeholder="Customer Name"
                      value={customer}
                      onChange={(e) => setCustomer(e.target.value)}
                    />
                    <input
                      type="number"
                      className="payment-input"
                      placeholder="Unpaid Amount"
                      value={unpaidAmount}
                      onChange={(e) => setUnpaidAmount(e.target.value)}
                    />
                    
                    {parseFloat(unpaidAmount || 0) > 0 && (
                      <div className="paid-amount-section">
                        <h4>Payment for Paid Amount</h4>
                        <p>Select payment method for the paid portion</p>
                        <div className="payment-methods">
                          <button 
                            className={`payment-method-button ${secondaryPaymentMethod === 'cash' ? 'selected' : ''}`}
                            onClick={() => setSecondaryPaymentMethod('cash')}
                          >
                            Cash
                          </button>
                          <button 
                            className={`payment-method-button ${secondaryPaymentMethod === 'bank' ? 'selected' : ''}`}
                            onClick={() => setSecondaryPaymentMethod('bank')}
                          >
                            Bank
                          </button>
                        </div>
                        
                        {secondaryPaymentMethod === 'bank' && (
                          <div className="bank-options">
                            {bankOptions.map(bank => (
                              <button
                                key={bank}
                                className={`bank-option ${secondarySelectedBank === bank ? 'selected' : ''}`}
                                onClick={() => setSecondarySelectedBank(bank)}
                              >
                                {bank}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Transaction Date:</label>
                <input
                  type="date"
                  className="form-control"
                  value={transactionDate}
                  onChange={(e) => setTransactionDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="form-group">
                <label>Comment:</label>
                <textarea
                  className="form-control"
                  placeholder="Add a comment (optional)"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </div>

              <button 
                className="checkout-button"
                onClick={handleCheckout}
                disabled={processing}
              >
                {processing ? 'Processing...' : 'Complete Sale'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sales;